import { supabase } from '../lib/supabase';

/**
 * Site Settings Service
 * Handles fetching site configuration including logos and hero images
 */

/**
 * Fetch site settings from the database
 * @returns {Promise<Object>} Site settings object with logo URLs
 */
export const fetchSiteSettings = async () => {
  try {
    const { data, error } = await supabase
      ?.from('site_settings')
      ?.select('horizontal_logo_url, vertical_logo_url, invite_code_required, invite_code')
      ?.single();

    if (error) {
      console.error('Error fetching site settings:', error);
      return { 
        horizontalLogo: null, 
        verticalLogo: null,
        inviteCodeRequired: false,
        inviteCode: null
      };
    }

    return {
      horizontalLogo: data?.horizontal_logo_url || null,
      verticalLogo: data?.vertical_logo_url || null,
      inviteCodeRequired: data?.invite_code_required || false,
      inviteCode: data?.invite_code || null
    };
  } catch (error) {
    console.error('Error in fetchSiteSettings:', error);
    return { 
      horizontalLogo: null, 
      verticalLogo: null,
      inviteCodeRequired: false,
      inviteCode: null
    };
  }
};

/**
 * Fetch active hero images for slideshow
 * @returns {Promise<Array>} Array of active hero images
 */
export const fetchHeroImages = async () => {
  try {
    const { data, error } = await supabase
      ?.from('hero_images')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching hero images:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchHeroImages:', error);
    return [];
  }
};

/**
 * Subscribe to real-time logo updates
 * @param {Function} callback - Callback function to handle logo updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToLogoUpdates = (callback) => {
  const subscription = supabase?.channel('site_settings_changes')?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'site_settings'
      },
      (payload) => {
        callback({
          horizontalLogo: payload?.new?.horizontal_logo_url || null,
          verticalLogo: payload?.new?.vertical_logo_url || null
        });
      }
    )?.subscribe();

  return () => {
    subscription?.unsubscribe();
  };
};

/**
 * Subscribe to real-time hero images updates
 * @param {Function} callback - Callback function to handle hero images updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToHeroImagesUpdates = (callback) => {
  const subscription = supabase?.channel('hero_images_changes')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'hero_images'
      },
      async () => {
        // Fetch updated hero images
        const images = await fetchHeroImages();
        callback(images);
      }
    )?.subscribe();

  return () => {
    subscription?.unsubscribe();
  };
};